/**
 * PatientListPage Component
 *
 * The patient list (ADR-012): search and one filter bar inside the list box,
 * quick filters beside it. Desktop: the quick filters sit in a left rail.
 * Mobile: single column, with the quick filters in a collapsible menu.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageGuideLink } from '@features/knowledge-base/links';
import { useGetPatientVisibilityQuery } from '@features/organizations';
import { useCan, useCanCreatePatients, useSubjectLanguageVariants } from '@features/user';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FileUpload as FileUploadIcon,
  MergeType as MergeIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking, useFeatureGate } from '@services/statsig';

import NewPatientModal from '../../../components/Patient/NewPatientModal';
import { capitalize } from '../../../utils/utils';
import { usePatientList } from '../hooks';
import { usePatientListFilterFields } from '../hooks/usePatientListFilterFields';
import { selectedValues } from '../utils/patientListQuery';

import { PatientEmptyState } from './PatientEmptyState';
import { PatientListFilterBox } from './PatientListFilterBox';
import { PatientListRow } from './PatientListRow';
import {
  PayerStatsSidebar,
  QuickFilters,
  type QuickFiltersProps,
  type RequirementFilterTarget,
} from './PayerStatsSidebar';

export const PatientListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { singular: subjectLanguage, singularCap, plural } = useSubjectLanguageVariants();
  const isAdmin = useCan('manage_organization');
  const canCreatePatients = useCanCreatePatients();
  const showImportButton = useFeatureGate('feature_patient_import');
  const { data: visibilityData } = useGetPatientVisibilityQuery();
  const isOrgWide = visibilityData?.mode === 'organization_wide';
  const [newPatientModalOpen, setNewPatientModalOpen] = useState(false);

  const { patientRequirementsRail } = useEventTracking();
  const [quickFiltersOpen, setQuickFiltersOpen] = useState(false);
  const { fields, canFilterRequirements } = usePatientListFilterFields();

  const {
    patients,
    isLoading,
    error,
    filtersUnavailable,
    searchTerm,
    page,
    totalRows,
    hasNextPage,
    hasPreviousPage,
    isEmpty,
    isSearchActive,
    filters,
    hasFilters,
    activityFilter,
    setFilters,
    toggleFilterValue,
    clearFilters,
    resetView,
    setSearchTerm,
    setPage,
    clearSearch,
    requirementColumns,
    refresh,
    navigateToPatient,
    handlePatientCreated,
  } = usePatientList({ initialPageSize: 20, canFilterRequirements });

  const handleImportClick = useCallback(() => {
    navigate('/patients/import');
  }, [navigate]);

  const handleNewPatientOpen = useCallback(() => {
    setNewPatientModalOpen(true);
  }, []);

  const handleNewPatientClose = useCallback(() => {
    setNewPatientModalOpen(false);
  }, []);

  const handlePatientSuccess = useCallback(
    (patientId: string) => {
      handlePatientCreated(patientId);
      setNewPatientModalOpen(false);
    },
    [handlePatientCreated],
  );

  const handlePreviousPage = useCallback(() => {
    setPage(page - 1);
  }, [setPage, page]);

  const handleNextPage = useCallback(() => {
    setPage(page + 1);
  }, [setPage, page]);

  // Rail rows keep their own funnel events alongside the shared filter event.
  const handleRequirementSelect = useCallback(
    (target: RequirementFilterTarget) => {
      patientRequirementsRail.filterSelected({
        patient_requirement_definition_id: target.id,
        // The kind, never the label: the label is practice-authored text that
        // patients read.
        kind: target.kind,
        missing_count: target.count,
      });
      toggleFilterValue('missing', target.id, 'sidebar');
    },
    [patientRequirementsRail, toggleFilterValue],
  );

  const handleRequirementDeselect = useCallback(
    (definition: { id: string; kind: string }) => {
      patientRequirementsRail.clearClicked({
        patient_requirement_definition_id: definition.id,
        kind: definition.kind,
        source: 'rail_row',
      });
      toggleFilterValue('missing', definition.id, 'sidebar');
    },
    [patientRequirementsRail, toggleFilterValue],
  );

  const quickFilterProps: QuickFiltersProps = {
    selected: {
      payers: selectedValues(filters, 'payers'),
      coverage: selectedValues(filters, 'coverage'),
      missing: selectedValues(filters, 'missing'),
    },
    onToggle: (category, value) => toggleFilterValue(category, value, 'sidebar'),
    onRequirementSelect: handleRequirementSelect,
    onRequirementDeselect: handleRequirementDeselect,
  };

  const statusLabel =
    activityFilter === null ? 'All statuses' : activityFilter ? 'Active' : 'Inactive';
  const resultStatement = isLoading
    ? `Loading ${plural}…`
    : `${totalRows.toLocaleString()} ${totalRows === 1 ? subjectLanguage : plural} · ${statusLabel}`;

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        display: 'flex',
        gap: 3,
        alignItems: 'flex-start',
      }}
    >
      {/* Quick filters rail — desktop only */}
      <PayerStatsSidebar {...quickFilterProps} />

      {/* Main content column */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            {/*
              Title row: the title, then the page sentence (GDN-3704). Flex gap
              reserves nothing while the sentence is absent; the sentence wraps
              under the title rather than pushing the actions when the row is
              narrow.
            */}
            <Stack
              direction="row"
              spacing={1.5}
              useFlexGap
              sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                {capitalize(subjectLanguage)}s
              </Typography>
              <PageGuideLink page="patients" />
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {isAdmin && (
              <Tooltip
                title={
                  !isOrgWide
                    ? 'Enable organization-wide patient sharing in Practice Settings to use this feature'
                    : ''
                }
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<MergeIcon />}
                    onClick={() => navigate('/patients/merge')}
                    disabled={!isOrgWide}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      borderRadius: 2,
                    }}
                  >
                    Merge Patients
                  </Button>
                </span>
              </Tooltip>
            )}
            {showImportButton && isAdmin && (
              <Tooltip
                title={
                  !isOrgWide
                    ? 'Enable organization-wide patient sharing in Practice Settings to use this feature'
                    : ''
                }
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<FileUploadIcon />}
                    onClick={handleImportClick}
                    disabled={!isOrgWide}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      borderRadius: 2,
                    }}
                  >
                    Import Patients
                  </Button>
                </span>
              </Tooltip>
            )}

            {canCreatePatients && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewPatientOpen}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  borderRadius: 2,
                }}
              >
                New {capitalize(subjectLanguage)}
              </Button>
            )}
          </Box>
        </Box>

        {/* Quick filters — phones. Mounted only once opened, so the
            requirement counts are spent only when someone can see them. */}
        <Box
          component="details"
          onToggle={event => setQuickFiltersOpen(event.currentTarget.open)}
          sx={{
            display: { md: 'none' },
            mb: 2,
            p: 1.5,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box component="summary" sx={{ cursor: 'pointer', py: 0.75 }}>
            Quick filters
          </Box>
          {quickFiltersOpen && (
            <Box sx={{ pt: 2 }}>
              <QuickFilters {...quickFilterProps} inMenu />
            </Box>
          )}
        </Box>

        {/* List Container */}
        <Paper
          component="section"
          elevation={0}
          aria-label={`${singularCap} list`}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <PatientListFilterBox
            plural={plural}
            fields={fields}
            filters={filters}
            onFiltersChange={next => setFilters(next, 'filter_bar')}
            search={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={clearSearch}
            onClearFilters={clearFilters}
            onResetView={resetView}
            resultStatement={resultStatement}
          />
          <Divider />

          {/* Loading state */}
          {isLoading && patients.length === 0 ? (
            <Box>
              {Array.from({ length: 8 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Skeleton variant="circular" sx={{ width: 44, height: 44 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton sx={{ width: '60%', height: 20, mb: 0.5 }} />
                    <Skeleton sx={{ width: '30%', height: 16 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : error ? (
            // Inside the list box, below the filters: a view from a link can
            // fail, and the way out is to change it, not to reload it.
            <Box sx={{ p: 3 }}>
              <Alert
                severity="error"
                action={
                  <Stack direction="row" spacing={1}>
                    <Button color="inherit" size="small" onClick={refresh}>
                      Retry
                    </Button>
                    <Button color="inherit" size="small" onClick={resetView}>
                      Clear search and filters
                    </Button>
                  </Stack>
                }
              >
                <AlertTitle>Error loading {plural}</AlertTitle>
                {error}
              </Alert>
            </Box>
          ) : filtersUnavailable ? (
            <Box sx={{ p: 4, textAlign: 'center' }} role="status">
              <Typography>This combination of filters is not available yet.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Use one value per filter and either a payer or a coverage filter, not both. Or
                search without filters.
              </Typography>
              <Button onClick={resetView} sx={{ mt: 1 }}>
                Clear search and filters
              </Button>
            </Box>
          ) : isEmpty && hasFilters ? (
            <Stack spacing={1} sx={{ p: 5, alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="h6">No {plural} match this combination</Typography>
              <Typography color="text.secondary">
                Remove a filter or change your search to broaden the list.
              </Typography>
              <Button variant="outlined" onClick={resetView}>
                Clear search and filters
              </Button>
            </Stack>
          ) : isEmpty ? (
            <PatientEmptyState
              isSearchActive={isSearchActive}
              searchTerm={searchTerm}
              onNewPatient={handleNewPatientOpen}
              onClearSearch={clearSearch}
              canCreatePatients={canCreatePatients}
            />
          ) : (
            <>
              {/* List */}
              <Box sx={{ position: 'relative' }}>
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: alpha(theme.palette.background.paper, 0.7),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}

                {patients.map(patient => (
                  <PatientListRow
                    key={patient.patient_id}
                    patient={patient}
                    onSelect={navigateToPatient}
                    requirement={requirementColumns[patient.patient_id]}
                  />
                ))}
              </Box>

              {/* Pagination */}
              {totalRows > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 1.5,
                    borderTop: `1px solid ${theme.palette.grey[100]}`,
                    bgcolor: alpha(theme.palette.grey[50], 0.5),
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Page {page + 1} of {Math.max(1, Math.ceil(totalRows / 20))}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={handlePreviousPage}
                      disabled={!hasPreviousPage || isLoading}
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleNextPage}
                      disabled={!hasNextPage || isLoading}
                      aria-label="Next page"
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* New Patient Modal */}
      <NewPatientModal
        open={newPatientModalOpen}
        onClose={handleNewPatientClose}
        onSuccessfulSubmission={handlePatientSuccess}
        source="patient_list"
      />
    </Box>
  );
};
